import React from 'react';
import PhoneNumber from 'awesome-phonenumber';
import compose from 'lodash/flowRight';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'next/router';

import Button from '../Button';
import EmailInput from './EmailInput';
import PhoneInput from './PhoneInput';
import PasswordInput from './PasswordInput';
import TokenInput from './TokenInput';
import Timer from './Timer';
import TermsAndPrivacy from './TermsAndPrivacy';
import { __ } from '../../lib/I18n';
import { setCookie, getAccessTokenLifetime, getUserData } from '../../lib/Util';

const styles = {
  separator: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '2.5rem'
  },
  line: {
    flex: 1,
    height: '1px',
    backgroundColor: '#E0E0E0',
  },
  separatorText: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: 16,
    margin: '0rem .68rem'
  }
};

/**
 * @typedef {{
 *  variables: {
 *    email?: string
 *    phone?: string
 *  }
 * }} GetAuthMethodParams
 * @typedef {{
 *  data: {
 *    getAuthenticationMethod: {
 *      authenticationMethod: string
 *      isNewUser: boolean
 *    }
 *  }
 * }} GetAuthMethodResponse
 *
 * @typedef {{
 *  variables: {
 *    input: {
 *      email?: string
 *      phone?: string
 *      password: string
 *    }
 *  }
 * }} AuthPasswordParams
 * @typedef {{
 *  data: {
 *    passwordAuthenticate: {
 *      user: {
 *        oauthProvider: { accessToken: string, refreshToken: string }
 *      }
 *    }
 *  }
 * }} AuthPasswordResponse
 *
 * @typedef {{
 *  variables: {
 *    input: {
 *      code: string
 *      address: string
 *    }
 *  }
 * }} AuthCodeParams
 * @typedef {{
 *  data: {
 *    codeAuthenticate: {
 *      user: {
 *        oauthProvider: { accessToken: string }
 *      }
 *    }
 *  }
 * }} AuthCodeResponse
 *
 * @typedef {{
 *  variables: {
 *    email?: string
 *    phone?: string
 *  }
 * }} SendCodeParams
 * @typedef {{
 *  variables: {
 *    email?: string
 *    phone?: string
 *  }
 * }} RecoverPasswordParams
 */

/**
 * @typedef {{
 *  handleSignIn?: (e: MouseEvent) => void,
 *  router: any,
 *  getAuthMethod: (params: GetAuthMethodParams) => Promise<GetAuthMethodResponse>
 *  authPassword: (params: AuthPasswordParams) => Promise<AuthPasswordResponse>
 *  recoverPassword: (params: RecoverPasswordParams) => Promise<void>
 *  authCode: (params: AuthCodeParams) => Promise<AuthCodeResponse>
 *  sendCode: (params: SendCodeParams) => Promise<void>
 *  store: import('../../lib/store').PopulatedStore
 * }} Props
 * @extends {React.Component<Props>}
 */

class LoginForm extends React.Component {
  /** @param {Props} props */
  constructor(props) {
    super(props);

    this.state = {
      addressInput: '',
      passwordInput: '',
      tokenInput: '',
      inputType: 'email',
      loginType: '',
      recoverPasswordSuccess: null,
      waiting: true,
      hasPassword: true,
      country: 'br'
    };
  }

  forgotPassword = () => {
    const { addressInput, loginType } = this.state;
    const { recoverPassword } = this.props;
    const email = loginType === 'email' ? addressInput : undefined;
    const phone = loginType === 'phone' ? addressInput : undefined;

    recoverPassword({
      variables: {
        email,
        phone
      }
    }).then(() => {
      this.setState({ recoverPasswordSuccess: true });
    }).catch(/** @param {any} e */(e) => {
      console.warn(e);
      this.setState({ recoverPasswordSuccess: false });
    });
  };

  resendCode = () => {
    const { addressInput, loginType } = this.state;
    const { sendCode } = this.props;
    const email = loginType === 'email' ? addressInput : undefined;
    const phone = loginType === 'phone' ? addressInput : undefined;

    sendCode({ variables: { email, phone } }).then(() => {
      this.setState({ waiting: true });
    }).catch(/** @param {any} e */(e) => {
      console.warn(e);
      this.setState({ waiting: false });
    });
  };

  /**
   * @param {'phone' | 'email'} loginType
   */
  submitUser = (loginType) => {
    const { addressInput, country } = this.state;
    const { getAuthMethod } = this.props;

    /**
     * @type {{ email?: string, phone?: string }}
     */
    const variables = {};

    if (loginType === 'email') variables.email = addressInput;
    else {
      // Formats phone adding the prefix according to the selected country
      const pn = new PhoneNumber(addressInput, country);
      const phone = pn.getNumber().split('+')[1];
      variables.phone = phone;
    }

    getAuthMethod({ variables }).then(/** @param {GetAuthMethodResponse} response */(response) => {
      const { authenticationMethod } = response.data.getAuthenticationMethod;

      if (authenticationMethod === 'password') {
        this.setState({
          inputType: 'password',
          loginType,
          addressInput: loginType === 'phone' ? variables.phone : addressInput
        });
      } else {
        this.setState({
          inputType: 'token',
          loginType,
          hasPassword: false,
          addressInput: loginType === 'phone' ? variables.phone : addressInput
        }, this.resendCode);
      }
    }).catch(/** @param {any} e */e => {
      console.warn(e);
      this.input?.onError();
    });
  };

  submitPassword = () => {
    const { addressInput, passwordInput, loginType } = this.state;
    const { authPassword } = this.props;

    /**
     * @type {AuthPasswordParams['variables']['input']}
     */
    const input = { password: passwordInput };

    if (loginType === 'email') input.email = addressInput;
    else input.phone = addressInput;

    authPassword({ variables: { input } }).then(/** @param {AuthPasswordResponse} response */(response) => {
      const { oauthProvider } = response.data.passwordAuthenticate.user;
      const { accessToken } = oauthProvider;

      this.afterSubmit(accessToken);
    }).catch(/** @param {any} e */(e) => {
      this.input?.onError();
      console.warn(e);
    });
  };

  submitCode = () => {
    const { tokenInput, addressInput } = this.state;
    const { authCode } = this.props;

    authCode({ variables: { input: { code: tokenInput, address: addressInput } } }).then(/** @param {AuthCodeResponse} response */(response) => {
      const { oauthProvider } = response.data.codeAuthenticate.user;
      const { accessToken } = oauthProvider;

      this.afterSubmit(accessToken);
    }).catch(/** @param {any} e */(e) => {
      console.warn(e);
      this.input?.onError();
    });
  };

  /**
   * @param {string} accessToken
   */
  afterSubmit = async (accessToken) => {
    let hasNoOrganizationsContracted = false;
    let user = null;

    if (accessToken) {
      setCookie(null, 'accessToken', accessToken, getAccessTokenLifetime());

      user = await getUserData(accessToken);
      hasNoOrganizationsContracted = !user?.organizations.length;
    }

    const { router, store } = this.props;

    if (user) await store.setUser(user);

    const route = !user?.notAuthorized && (!user?.acceptedTerms || hasNoOrganizationsContracted)
      ? '/serviceContract'
      : '/';

    if (user?.notAuthorized) await store.setErrorMessage('NO_ENTITY');

    router.push(route);
  };

  render() {
    const {
      addressInput, passwordInput, tokenInput, inputType, loginType,
      recoverPasswordSuccess, waiting, hasPassword, country
    } = this.state;

    return (
      <form onSubmit={(e) => e.preventDefault()}>
        {inputType === 'email' && (
          <EmailInput
            ref={(c) => { this.input = c; }}
            value={addressInput}
            onChange={/** @param {string} e*/(e) => this.setState({ addressInput: e })}
            onConfirm={() => this.submitUser('email')}
          />
        )}

        {inputType === 'phone' && (
          <PhoneInput
            ref={(c) => { this.input = c; }}
            value={addressInput}
            country={country}
            onChange={/** @param {string} e*/(e) => this.setState({ addressInput: e })}
            onChangeCountry={/** @param {string} e*/(e) => this.setState({ country: e })}
            onConfirm={() => this.submitUser('phone')}
          />
        )}

        {inputType === 'password' && (
          <PasswordInput
            ref={(c) => { this.input = c; }}
            value={passwordInput}
            placeholder="Digite sua senha aqui..."
            sendTo={addressInput}
            loginType={loginType}
            onChange={/** @param {string} e*/(e) => this.setState({ passwordInput: e })}
            onConfirm={() => this.submitPassword()}
            recoverPassword={() => this.forgotPassword()}
            recoverSuccess={recoverPasswordSuccess}
            closeAlert={() => this.setState({ recoverPasswordSuccess: null })}
          />
        )}

        {inputType === 'token' && (
          <TokenInput
            ref={(c) => { this.input = c; }}
            value={tokenInput}
            sendTo={addressInput}
            loginType={loginType}
            onChange={/** @param {string} e*/(e) => this.setState({ tokenInput: e })}
            onConfirm={() => this.submitCode()}
            onError={() => this.setState({ tokenInput: '' })}
            resendCode={() => this.resendCode()}
          />
        )}

        {(inputType === 'email' || inputType === 'phone') ? (
          <Button
            onClick={() => this.input?.onConfirm()}
            text={inputType === 'email' ? __('Continuar com email') : __('Continuar com telefone')}
            style={{ justifyContent: 'center', width: '23rem' }}
          />
        ) : (
          <Button
            onClick={() => this.input?.onConfirm()}
            text={__('Entrar no financeiro')}
            style={{ justifyContent: 'center', width: '23rem' }}
          />
        )}

        {inputType === 'token' && (
          <div style={{ marginTop: '2rem' }}>
            {waiting ? (
              <Timer
                interval={30}
                content={__('Wait')}
                textColor="#6C6C6C"
                onFinishTimer={() => this.setState({ waiting: false })}
              />
            ) : (
              <a
                onClick={this.resendCode}
                onKeyPress={this.resendCode}
                style={{
                  textDecoration: 'underline',
                  fontSize: 16,
                  fontWeight: 400,
                  color: '#6C6C6C',
                  marginLeft: '25%',
                  cursor: 'pointer' }}
              >
                {__('Não recebi o código')}
              </a>
            )}
          </div>
        )}

        {hasPassword && (
          <div style={styles.separator}>
            <div style={styles.line} />
            <p style={styles.separatorText}>{__('ou')}</p>
            <div style={styles.line} />
          </div>
        )}

        {(inputType === 'email' || inputType === 'phone') ? (
          <Button
            text={inputType === 'email' ? __('Entrar com número do celular') : __('Entrar com email')}
            onClick={() => this.setState({ inputType: inputType === 'email' ? 'phone' : 'email', addressInput: '' })}
            style={{ justifyContent: 'center', width: '23rem', marginTop: '2.5rem' }}
            transparent
          />
        ) : hasPassword && (
          <Button
            text={inputType === 'password' ? loginType === 'email' ? __('Receber código por email') : __('Receber código por SMS') : __('Entrar com senha')}
            onClick={() => {
              this.resendCode();
              this.setState({ inputType: inputType === 'password' ? 'token' : 'password' });
            }}
            style={{ justifyContent: 'center', width: '23rem', marginTop: '2.5rem' }}
            transparent
          />
        )}
        <TermsAndPrivacy />
      </form>
    );
  }
}

export default compose(
  withRouter,
  inject('store'),
  graphql(gql`
    mutation KrabsRecoverPassword($email: String, $phone: String) {
      sendCode(
        input: {
          email: $email,
          phone: $phone,
          recoverPassword: true,
          isNewCode: true,
          source: WEB
        }
      ) {
        __typename
      }
    }
  `, {
    name: 'recoverPassword'
  }),
  graphql(gql`
    mutation KrabsGetAuthMethod($email: String, $phone: String) {
      getAuthenticationMethod(input: { email: $email, phone: $phone }) {
        authenticationMethod
        isNewUser
      }
    }`, {
    name: 'getAuthMethod'
  }),
  graphql(gql`
    mutation KrabsPasswordAuthenticate($input: PasswordAuthenticateInput!) {
      passwordAuthenticate(input: $input) {
        user {
          oauthProvider {
            accessToken
            refreshToken
          }
        }
      }
    }`, {
    name: 'authPassword'
  }),
  graphql(gql`
    mutation KrabsCodeAuthenticate($input: CodeAuthenticateInput!) {
      codeAuthenticate(input: $input) {
        user {
          oauthProvider {
            accessToken
            refreshToken
          }
        }
      }
    }`, {
    name: 'authCode'
  }),
  graphql(gql`
    mutation KrabsSendCode($email: String, $phone: String) {
      sendCode(
        input: {
          email: $email,
          phone: $phone,
          invite: true,
          source: WEB,
          isNewCode: true
        }
      ) {
        __typename
      }
    }`, {
    name: 'sendCode'
  }),
  observer
)(LoginForm);
